/**
 * Layout and text primitives shared by every screen.
 */

import type { ReactNode } from 'react';
import { useT } from '../../i18n';

export function Card({
  title,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={className ? `card ${className}` : 'card'}>
      {title || actions ? (
        <header className="card__header">
          <h2 className="card__title">{title}</h2>
          {actions}
        </header>
      ) : null}
      {children}
    </section>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="page-header">
      <div className="page-header__text">
        <h1>{title}</h1>
        {subtitle ? <div className="page-header__subtitle">{subtitle}</div> : null}
      </div>
      {actions}
    </div>
  );
}

/**
 * A headline number.
 *
 * `value` being null renders as an explicit "not recorded" rather than a zero
 * or a dash, because a dash reads as "small" and a zero reads as a measurement.
 */
export function Stat({
  label,
  value,
  foot,
  absentLabel,
}: {
  label: ReactNode;
  value: ReactNode | null;
  foot?: ReactNode;
  absentLabel?: string;
}) {
  const t = useT();
  const absent = absentLabel ?? t.common.notRecordedTitle;
  return (
    <div className="card">
      <div className="stat__label">{label}</div>
      <div className={value === null || value === undefined ? 'stat__value stat__value--absent' : 'stat__value'}>
        {value === null || value === undefined ? absent : value}
      </div>
      {foot ? <div className="stat__foot">{foot}</div> : null}
    </div>
  );
}

export function Badge({
  tone = 'neutral',
  title,
  children,
}: {
  tone?: string;
  title?: string;
  children: ReactNode;
}) {
  return (
    <span className={`badge badge--${tone}`} title={title}>
      {children}
    </span>
  );
}

/** Renders a value, or an explicit absence marker when it is not known. */
export function Value({
  children,
  absent,
}: {
  children: string | number | null | undefined;
  absent?: string;
}) {
  const t = useT();
  if (children === null || children === undefined || children === '') {
    return <span className="absent">{absent ?? t.common.unknown}</span>;
  }
  return <>{children}</>;
}

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: ReadonlyArray<{ id: T; label: string }>;
  active: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={tab.id === active}
          className={tab.id === active ? 'tab tab--active' : 'tab'}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function Notice({
  severity = 'WARN',
  children,
}: {
  severity?: 'INFO' | 'WARN' | 'CRITICAL';
  children: ReactNode;
}) {
  const icon = severity === 'CRITICAL' ? '!' : severity === 'WARN' ? '!' : 'i';
  return (
    <div className={`notice notice--${severity}`}>
      <span className="notice__icon" aria-hidden="true">
        {icon}
      </span>
      <span>{children}</span>
    </div>
  );
}

/**
 * Warnings attached to a computed result.
 *
 * Every inference in this product carries a `warnings: string[]`, and those
 * strings are the honest caveats on the number next to them. They are rendered
 * adjacent to the result, never collapsed away by default.
 */
export function WarningList({
  warnings,
  severity = 'WARN',
}: {
  warnings: readonly string[];
  severity?: 'INFO' | 'WARN' | 'CRITICAL';
}) {
  if (warnings.length === 0) return null;
  return (
    <div className="notice-list">
      {warnings.map((warning) => (
        <Notice key={warning} severity={severity}>
          {warning}
        </Notice>
      ))}
    </div>
  );
}

/** The reasons behind a recommendation (spec section 18). */
export function ReasonList({ reasons }: { reasons: readonly string[] }) {
  const t = useT();
  if (reasons.length === 0) {
    return <p className="muted small">{t.inference.noReasons}</p>;
  }
  return (
    <ul className="small muted" style={{ margin: '4px 0 0', paddingLeft: 18 }}>
      {reasons.map((reason) => (
        <li key={reason}>{reason}</li>
      ))}
    </ul>
  );
}
